import { useState } from "react";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { requestPermission } from "../../firebase";
import {
  hasAnyPushPref,
  hasRegisteredPushDevice,
  syncSessionUserNotificationPrefs,
} from "../../shared/utils/pushHealth";
import { USER_ACCESS_SUBCARD_TITLE } from "./userAccessFormUi";

const getBrowserPermission = () => {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission || "default";
};

const UserAccessPushDeviceStatus = ({ data, setData, isSelf }) => {
  const { t } = useTranslation();
  const [registeringDevice, setRegisteringDevice] = useState(false);

  if (!hasAnyPushPref(data)) {
    return (
      <p className="user-access-push-hint">
        <i className="fas fa-info-circle shrink-0" aria-hidden="true" />
        <span>{t("settingsUsers.pushDeviceHint")}</span>
      </p>
    );
  }

  const browserPermission = getBrowserPermission();
  const browserToken = Cookies.get("fcmToken");
  const serverRegistered = hasRegisteredPushDevice(data);
  const tokenCount =
    typeof data?.fcmTokenCount === "number"
      ? data.fcmTokenCount
      : Array.isArray(data?.fcmTokens)
        ? data.fcmTokens.length
        : 0;
  const thisBrowserReady =
    browserPermission === "granted" && Boolean(browserToken);

  const handleActivateThisComputer = async () => {
    if (registeringDevice) return;
    setRegisteringDevice(true);
    try {
      const permission = await requestPermission(setRegisteringDevice, null);
      if (permission !== "granted") {
        toast.warn(t("settingsUsers.pushRegisterDenied"));
        return;
      }

      const nextTokenCount = Math.max(tokenCount, 1);
      setData((prev) => ({
        ...(prev || {}),
        fcmTokenCount: nextTokenCount,
        fcmTokens:
          Array.isArray(prev?.fcmTokens) && prev.fcmTokens.length > 0
            ? prev.fcmTokens
            : browserToken
              ? [browserToken]
              : prev?.fcmTokens || [],
      }));

      const synced = syncSessionUserNotificationPrefs({
        ...data,
        fcmTokenCount: nextTokenCount,
      });
      if (synced) {
        window.dispatchEvent(
          new CustomEvent("sessionUserNotificationsUpdated", {
            detail: { user: synced },
          }),
        );
      }

      toast.success(t("settingsUsers.pushRegisterSuccess"));
    } catch (error) {
      console.error(error);
      toast.error(t("settingsUsers.pushRegisterFailed"));
    } finally {
      setRegisteringDevice(false);
    }
  };

  const browserStatusKey = (() => {
    if (thisBrowserReady) return "settingsUsers.pushStatusBrowserReady";
    if (browserPermission === "granted") {
      return "settingsUsers.pushStatusBrowserPermissionOnly";
    }
    if (browserPermission === "denied") {
      return "settingsUsers.pushStatusBrowserDenied";
    }
    return "settingsUsers.pushStatusBrowserNotReady";
  })();

  const showActivateCta =
    isSelf && browserPermission !== "denied" && !thisBrowserReady;

  return (
    <div className="user-access-push-status">
      <h4 className={USER_ACCESS_SUBCARD_TITLE}>
        {t("settingsUsers.pushStatusTitle")}
      </h4>
      <p className="user-access-push-status__intro">
        {t("settingsUsers.pushStatusIntro")}
      </p>

      <div className="user-access-push-status__card">
        <div className="user-access-push-status__row">
          <span className="user-access-push-status__label">
            {t("settingsUsers.pushStatusAccount")}
          </span>
          <span className="user-access-push-status__value">
            {data?.email || "—"}
          </span>
        </div>

        {isSelf ? (
          <div className="user-access-push-status__row">
            <span className="user-access-push-status__label">
              {t("settingsUsers.pushStatusThisBrowser")}
            </span>
            <span className="user-access-push-status__value">
              {t(browserStatusKey)}
            </span>
          </div>
        ) : null}

        <div className="user-access-push-status__row">
          <span className="user-access-push-status__label">
            {t("settingsUsers.pushStatusRegisteredDevices")}
          </span>
          <span className="user-access-push-status__value">
            {t("settingsUsers.pushStatusRegisteredDevicesCount", {
              count: tokenCount,
            })}
          </span>
        </div>
      </div>

      {isSelf && browserPermission === "granted" && !serverRegistered ? (
        <p className="user-access-push-status__note">
          <i className="fas fa-info-circle shrink-0" aria-hidden="true" />
          <span>{t("settingsUsers.pushStatusStaleBrowserNote")}</span>
        </p>
      ) : null}

      {showActivateCta ? (
        <button
          type="button"
          className="user-access-push-status__cta modal-button modal-button-save"
          disabled={registeringDevice}
          onClick={() => void handleActivateThisComputer()}
        >
          {registeringDevice
            ? t("settingsUsers.pushRegistering")
            : t("settingsUsers.pushActivateThisComputer")}
        </button>
      ) : null}

      <div className="user-access-push-status__phone">
        <p className="user-access-push-status__phone-title">
          {t("settingsUsers.pushPhoneTitle")}
        </p>
        <ol className="user-access-push-status__phone-steps">
          <li>{t("settingsUsers.pushPhoneStep1")}</li>
          <li>{t("settingsUsers.pushPhoneStep2")}</li>
          <li>{t("settingsUsers.pushPhoneStep3")}</li>
        </ol>
      </div>
    </div>
  );
};

export default UserAccessPushDeviceStatus;
